import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Privacy-friendly analytics via Plausible (cookieless, no personal data, LGPD/GDPR-safe).
 * Inert until you set VITE_PLAUSIBLE_DOMAIN in your environment, so nothing is loaded
 * or tracked by default. Swap the src for an Umami snippet if you self-host instead.
 */
const DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;

declare global {
  interface Window {
    plausible?: (
      event: string,
      options?: { props?: Record<string, string | number | boolean> }
    ) => void;
  }
}

const Analytics = () => {
  const location = useLocation();
  const firstLoad = useRef(true);

  // Inject the script once (script.js fires the initial pageview itself).
  useEffect(() => {
    if (!DOMAIN || document.querySelector("script[data-domain]")) return;
    const s = document.createElement("script");
    s.defer = true;
    s.setAttribute("data-domain", DOMAIN);
    s.src = "https://plausible.io/js/script.js";
    document.head.appendChild(s);
  }, []);

  // Track SPA route changes (react-router uses pushState, which the script doesn't auto-track).
  useEffect(() => {
    if (!DOMAIN) return;
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    window.plausible?.("pageview");
  }, [location.pathname, location.search]);

  return null;
};

export default Analytics;
